<script setup lang="ts">

import MainContainer from '@/components/MainContainer.vue'
import { computed, type ComputedRef, inject, markRaw, onBeforeMount, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import DailyComboSection from '@/components/DailyComboSection.vue'
import { usePlayer } from '@/composables/game/player'
import { useConfig } from '@/composables/game/config'
import PlayerUpgradeCard from '@/components/PlayerUpgradeCard.vue'
import { ECOMMERCE_ITEM_PRICE_TYPE, type PlayerUpgrade } from '@/types/shared/player-config'
import BuyUpgradeModal from '@/components/modals/BuyUpgradeModal.vue'
import TapSection from '@/components/TapSection.vue'
import { useAnimateCoins, useFlyingCoins } from '@/composables/game/flying-coins'
import CoinsTotalWrapper from '@/components/CoinsTotalWrapper.vue'
import { T, useTranslate } from '@tolgee/vue'
import BadgeOverlay from '@/components/overlays/BadgeOverlay.vue'
import type { Badge } from '@/types/shared/player'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { useSpecialCards } from '@/composables/game/special-cards'
import { useNow } from '@/composables/game/time'
import { UserGuide } from '@/types/onboarding'
import { useGuide } from '@/composables/game/guide'
import UserGuideModal from '@/components/modals/UserGuideModal.vue'
import AnalyticsImage from '@/assets/images/gifs/analytics.gif'
import {
  useBatchFastForward,
  useCardBattle,
  useNewUpgradesChecker,
  useUpgradeCooldown,
} from '@/composables/game/upgrade'
import DailyComboLastItemModal from '@/components/modals/DailyComboLastItemModal.vue'
import DailyComboOverlay from '@/components/overlays/DailyComboOverlay.vue'
import SpinnerIcon from '@/components/icons/SpinnerIcon.vue'
import { useToaster } from '@/composables/game/toasters'
import { useDailyCombo } from '@/composables/game/daily'
import { useSkin } from '@/composables/game/skin'
import BannerCarousel from '@/components/BannerCarousel.vue'
import WebApp from '@twa-dev/sdk'
import BatchSpeedUpBanner from '@/components/BatchSpeedUpBanner.vue'
import BatchSpeedUpModal from '@/components/BatchSpeedUpModal.vue'

const isTapping = ref(false)
const { player, upgrade, isUpgrading, hasUpgraded, newBadges } = usePlayer()
const { config, upgrades } = useConfig()
const { addHapticFeedback } = useHapticFeedback()
const { getSpecialBgColor } = useSpecialCards()
const { nowDateMinuteTimestamp } = useNow()
const { shouldShowGuide, removeGuide } = useGuide()
const { t } = useTranslate()
const { formattedTime, remainedTime } = useUpgradeCooldown(computed(() => selectedUpgrade.value))
const { startCoinAnimation } = useFlyingCoins()
const { notify } = useToaster()
const { doAnimateCoins } = useAnimateCoins()
const {
  foundCombo,
  foundComboCards,
  playerStatsAfterDailyCombo,
  resetPlayerStatsForDailyCombo,
} = useDailyCombo()
const { currentSkin } = useSkin()
const { isInBattleConflict } = useCardBattle()
const { isUpgradeNew } = useNewUpgradesChecker()
const {
  batchFastForwardPurchased,
  canBatchFastForward,
  batchDiscount,
  ecommerceItem: batchFastForwardEcommerceItem,
} = useBatchFastForward()

const coinsToShow = inject<ComputedRef<number>>('coinsToShow') as ComputedRef<number>
const showGuideModal = ref(false)

const selectedSection = ref(config.value.upgradeSections[0])
const isSpecialsSectionSelected = computed(() => selectedSection.value.toLocaleLowerCase() === 'specials')
const selectedSectionUpgrades = computed(() => {
  let upgradesArr = upgrades.value.filter(u => u.section === selectedSection.value)

  // Highlight sorting first
  upgradesArr = upgradesArr.sort((u1, u2) => {
    if (u1.highlight) return -1
    if (u2.highlight) return 1
    return 0
  })

  if (isSpecialsSectionSelected.value) {
    const now = nowDateMinuteTimestamp.value
    const notExpired = upgradesArr.filter(u => (u.expiresOn ?? Infinity) > now)
    const expired = upgradesArr.filter(u => (u.expiresOn ?? Infinity) <= now)
    notExpired.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
    expired.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
    upgradesArr = [...notExpired, ...expired]
  } else {
    // Keep previous expiresOn sort for non-specials
    upgradesArr = upgradesArr.sort((u1, u2) => {
      const u1Expiry = u1.expiresOn ?? Infinity
      const u2Expiry = u2.expiresOn ?? Infinity
      if (u1Expiry > nowDateMinuteTimestamp.value && u2Expiry > nowDateMinuteTimestamp.value) return 0
      if (u1Expiry < nowDateMinuteTimestamp.value && u2Expiry < nowDateMinuteTimestamp.value) return 0
      return u1Expiry > nowDateMinuteTimestamp.value ? -1 : 1
    })
  }
  return upgradesArr
})

const selectedUpgrade = ref<PlayerUpgrade | null>(null)
const showBuyUpgradeModal = computed(() => selectedUpgrade.value !== null)
const foundBadge = ref<Badge | null>(null)
const showBadgeOverlay = computed(() => foundBadge.value !== null)
const canUpgradeForStars = computed(() => player.value.level >= 5)
const scrollPosition = ref(0)

const showDailyComboOverlay = ref(false)
const showLastComboModal = ref(false)

const foundComboLength = computed(() => foundCombo.value.length)

const cooldownForAnimation = ref(0)
const cooldownAnimationInitialStartDashoffset = ref(0)
const elapsedTimeForAnimation = ref(0)

const hasCooldown = computed(() => !!selectedUpgrade.value && selectedUpgrade.value.cooldown > 0 && formattedTime.value !== undefined)

const showBanner = computed(() => WebApp.initDataUnsafe.user?.username !== 'Fom98')

const batchSpeedUpModalProgramaticallyPoppedUp = ref(false)
const showBatchSpeedUpModal = ref(false)

async function doUpgrade(purchase: boolean, priceType?: ECOMMERCE_ITEM_PRICE_TYPE) {
  if (!selectedUpgrade.value) return
  await upgrade(selectedUpgrade.value.id, selectedUpgrade.value.price, purchase ? selectedUpgrade.value.ecommerce.fullUpgrade.prices.find(price => price.type === (priceType ?? ECOMMERCE_ITEM_PRICE_TYPE.STAR)) : null)
}

function getCardBgColor(index: number) {
  if (isSpecialsSectionSelected.value) {
    return getSpecialBgColor(index)
  }
  return undefined
}

function onPageScroll(event: Event) {
  scrollPosition.value = (event.target as Element).scrollTop
}

function resetCooldown() {
  if (selectedUpgrade.value) {
    cooldownForAnimation.value = remainedTime.value
    elapsedTimeForAnimation.value = selectedUpgrade.value.cooldown - (cooldownForAnimation.value)
    cooldownAnimationInitialStartDashoffset.value = -(elapsedTimeForAnimation.value / selectedUpgrade.value.cooldown) * 100
  }
}

watch(foundComboLength, (foundComboLength) => {
  if (foundComboLength === 1 || foundComboLength === 2) {
    setTimeout(() => showDailyComboOverlay.value = true, 800)
  }
})

watch(playerStatsAfterDailyCombo, (playerStatsForDailyCombo, oldValue) => {
  if (!oldValue && !!playerStatsForDailyCombo) {
    setTimeout(() => showLastComboModal.value = true, 300)
  }
})

watch(showLastComboModal, showLastComboModal => {
  if (showLastComboModal) return
  notify({
    title: t.value('daily_combo.requesting_claim', 'Requesting claim...'),
    icon: markRaw(SpinnerIcon),
    duration: 1000,
  })
  doAnimateCoins()
  setTimeout(() => {
    notify({ title: t.value('daily_combo.claim_success', 'Claim success!'), type: 'success' })
    startCoinAnimation()
    resetPlayerStatsForDailyCombo()
  }, 1000)
})

function onHideBatchSpeedUpModal() {
  showBatchSpeedUpModal.value = false
  setTimeout(() => batchFastForwardPurchased.value = false, 300)
}

onBeforeMount(() => {
  showGuideModal.value = shouldShowGuide(UserGuide.Improve)
})

onMounted(() => {
  const scrollTarget = document.querySelector('.scroll-container')
  if (scrollTarget) {
    scrollTarget.addEventListener('scroll', onPageScroll)
  }
})

onBeforeUnmount(() => {
  const scrollTarget = document.querySelector('.scroll-container')
  if (scrollTarget) {
    scrollTarget.removeEventListener('scroll', onPageScroll)
  }
})

watch(hasUpgraded, () => {
  addHapticFeedback()
  startCoinAnimation(50)
  if (!selectedUpgrade.value) return

  selectedUpgrade.value = null
})

watch(newBadges, newBadges => {
  if (!newBadges || !newBadges.length) return
  setTimeout(() => foundBadge.value = newBadges[0], 150)
})

watch(selectedSection, () => {
  const scrollTarget = document.querySelector('.scroll-container')
  if (scrollTarget && scrollTarget.scrollTop > 274) {
    scrollTarget.scrollTop = 274
  }
})

watch(selectedUpgrade, (newVal) => {
  if (newVal) {
    resetCooldown()
    return
  }
})

watch(canBatchFastForward, canBatchFastForward => {
  if (!canBatchFastForward || batchSpeedUpModalProgramaticallyPoppedUp.value) return
  showBatchSpeedUpModal.value = true
  batchSpeedUpModalProgramaticallyPoppedUp.value = true
})

onMounted(() => {
  if (hasCooldown.value) {
    resetCooldown()
  }
})
</script>

<template>
  <MainContainer
    class="!pt-[18px] pb-6"
    :class="{'touch-none': isTapping}"
  >
    <div class="w-full h-full flex-1 flex flex-col">
      <BannerCarousel
        v-if="showBanner"
        class="mb-8"
      />
      <DailyComboSection :class="scrollPosition < 40 ? 'z-[81]' : 'z-[1]'" />
      <CoinsTotalWrapper
        class="z-[1] my-6 justify-center"
        :coins="coinsToShow"
      />
      <BatchSpeedUpBanner
        v-if="canBatchFastForward"
        :discount="batchDiscount"
        @click="showBatchSpeedUpModal = true"
      />
      <div
        class="z-[81] w-full flex flex-wrap items-center sticky top-[66px] bg-dark-top-layer rounded-xl p-0.5 bounce-in-left"
      >
        <div
          v-for="section in config.upgradeSections"
          :key="section"
          class="flex-1 text-xs leading-4 font-bold py-2 rounded-[10px] text-center"
          :class="section === selectedSection ? 'bg-dark-top-most-layer text-light-white' : 'text-white/[.38]'"
          @click="addHapticFeedback(); selectedSection = section"
        >
          <T
            :key-name="`cards.section.${section.toLowerCase().replace(/ /g, '_')}`"
            :default-value="section"
          />
        </div>
      </div>
      <div class="mt-3 mb-8 grid gap-3 grid-cols-2 z-[2]">
        <PlayerUpgradeCard
          v-for="(upgradeObj, index) in selectedSectionUpgrades"
          :key="upgradeObj.id"
          class="flex-1"
          :upgrade-list="upgrades"
          :upgrade="upgradeObj"
          :player="player"
          :is-special="isSpecialsSectionSelected"
          :is-highlight="!!upgradeObj.highlight"
          :bg-color="getCardBgColor(index)"
          :can-upgrade-for-stars="canUpgradeForStars"
          :index="index"
          :is-in-battle-conflict="isInBattleConflict(upgradeObj)"
          :is-new="isUpgradeNew(upgradeObj.id)"
          @card-click="selectedUpgrade = upgradeObj"
        />
      </div>
      <TapSection
        :back-image="currentSkin.imageUrl"
        hide-inner-image
        tap-container-class="z-[1]"
        fade-image-class="hidden"
        image-wrapper-class="rounded-none"
        image-inner-wrapper-class="rounded-none"
        custom-text="ho"
        @is-tapping="isTapping = $event"
      />
    </div>

    <BuyUpgradeModal
      v-if="showBuyUpgradeModal && selectedUpgrade"
      :show="showBuyUpgradeModal"
      :player="player"
      :upgrade="selectedUpgrade"
      :is-upgrading="isUpgrading"
      :elapsed-time="elapsedTimeForAnimation"
      :animation-cooldown="cooldownForAnimation"
      :animation-offset="cooldownAnimationInitialStartDashoffset"
      :has-cooldown="hasCooldown"
      :can-upgrade-for-stars="canUpgradeForStars"
      @hide="selectedUpgrade = null"
      @upgrade="doUpgrade"
      @on-new-upgrade="(value: PlayerUpgrade | null) => selectedUpgrade = value"
    />

    <BadgeOverlay
      v-if="showBadgeOverlay"
      :class="showBadgeOverlay ? 'z-[999] opacity-100' : '-z-[1] opacity-0'"
      :name="foundBadge?.name"
      :image="foundBadge?.imageUrl"
      :show="showBadgeOverlay"
      @click="foundBadge = null"
    />

    <UserGuideModal
      v-if="showGuideModal"
      :show="showGuideModal"
      :image="AnalyticsImage"
      :title="t('guide.improve_title', 'Boost your progress with cards')"
      :description="t('guide.improve_description', `Buy cards to boost your trading per hour and grow your passive income—even while offline! <br/><br/> Check-in daily for card combos that unlock extra bonuses!`)"
      @hide="showGuideModal = false; removeGuide(UserGuide.Improve)"
    />
    <DailyComboOverlay
      v-if="showDailyComboOverlay"
      :combo-cards="foundComboCards"
      :show="showDailyComboOverlay"
      @click="showDailyComboOverlay = false; resetPlayerStatsForDailyCombo()"
    />

    <DailyComboLastItemModal
      v-if="showLastComboModal"
      :show="showLastComboModal"
      @hide="showLastComboModal = false"
    />

    <BatchSpeedUpModal
      v-if="showBatchSpeedUpModal && (canBatchFastForward || batchFastForwardPurchased) && !!batchFastForwardEcommerceItem"
      :show="showBatchSpeedUpModal && (canBatchFastForward || batchFastForwardPurchased)"
      :discount="batchDiscount"
      :ecommerce-item="batchFastForwardEcommerceItem"
      @hide="onHideBatchSpeedUpModal"
    />
  </MainContainer>
</template>

<style scoped>

</style>
